import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronDown } from "lucide-react";

import { useCombobox } from "../hooks/useCombobox";

type ComboboxSelectionButtonProps = {
  id?: string;
  startEnhancer?: React.ReactNode;
  title: string;
  isPlaceholder?: boolean;
  placeholder?: string;
  layoutClassName?: string;
  isDisabled?: boolean;
  unsafe_className?: string;
  size?: "sm" | "base";
  variant?: "primary" | "secondary";
  validityState?: "valid" | "invalid";
  titleAlignment?: "start" | "center";
  endEnhancer?: React.ReactNode;
};

const variantClassName = {
  primary: "bg-sheet hover:bg-surface text-default",
  secondary: "bg-white hover:bg-surface",
};

const isActiveVariantMap = {
  primary: "bg-surface",
  secondary: "bg-surface",
};

export const ComboboxSelectionButton = React.forwardRef<
  HTMLButtonElement,
  ComboboxSelectionButtonProps
>((props, ref) => {
  const { open, setOpen } = useCombobox();
  const {
    startEnhancer,
    title,
    isPlaceholder,
    layoutClassName,
    isDisabled,
    unsafe_className,
    size = "base",
    variant = "primary",
    id,
    validityState,
  } = props;
  const endEnhancer = (() => {
    if (props.endEnhancer !== undefined) {
      return props.endEnhancer;
    }

    return (
      <ChevronDown
        size={size === "sm" ? 12 : 16}
        className="text-muted shrink-0"
      />
    );
  })();

  return (
    <button
      id={id}
      className={twMerge(
        "border-0.5 border-component-border shadow-2xs gap-1",
        size === "sm" && "h-6 px-2 py-1 text-xs",
        size === "base" && "h-8 p-2 text-sm",
        "flex items-center overflow-hidden rounded-md font-normal leading-5 transition duration-300",
        endEnhancer &&
          (props.titleAlignment ?? "center") === "center" &&
          "justify-between",
        variantClassName[variant],
        isDisabled && "cursor-not-allowed opacity-50",
        validityState === "invalid" && "bg-rose-soft outline-rose outline-1",
        // If combobox is open, make background the same as hover state
        open && isActiveVariantMap[variant],
        layoutClassName,
        unsafe_className,
      )}
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={open}
      disabled={isDisabled}
      onClick={() => setOpen(!open)}
    >
      {startEnhancer}
      <div
        className={twMerge(
          "flex truncate",
          endEnhancer &&
            props.titleAlignment === "start" &&
            "min-w-0 flex-1 justify-start",
        )}
      >
        <span
          className={twMerge("truncate", isPlaceholder && "text-placeholder")}
        >
          {title}
        </span>
      </div>
      {endEnhancer}
    </button>
  );
});

ComboboxSelectionButton.displayName = "ComboboxSelectionButton";
